import { writable, derived, get } from 'svelte/store';
import { browser } from '$app/environment';


// A browser keeps about 5 MB per site. Past that, setItem throws, and a store
// subscriber that throws stops Svelte 4 from running any later store update:
// the whole page froze until a reload. So a failed save makes room once and
// retries, and otherwise is skipped. The page keeps working; only that change
// is not kept over a reload.
function saveToStorage(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
        return;
    } catch (e) {
        if (!makeRoom()) {
            console.warn("Not saved to browser storage: " + key, e);
            return;
        }
    }
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        console.warn("Not saved to browser storage: " + key, e);
    }
}

// The request record is the part of browser storage that grows on its own.
// Drop its older half from storage straight away, so the failed save can be
// retried, and from the store.
function makeRoom() {
    try {
        const record = get(requests_component_lookup) ?? {};
        const ids = Object.keys(record);
        if (ids.length < 2) return false;
        const kept = Object.fromEntries(
            ids.slice(Math.floor(ids.length / 2)).map((id) => [id, record[id]]),
        );
        localStorage.setItem("requests_component_lookup", JSON.stringify(kept));
        requests_component_lookup.set(kept);
        return true;
    } catch (e) {
        // Also covers a save that fails before the record exists.
        return false;
    }
}

// A save rewrites the whole value, and global_state changes with every
// streamed token, so one long answer meant thousands of full rewrites.
// Changes are collected and saved at most once a second, and straight away
// when the tab is hidden or closed.
const SAVE_INTERVAL_MS = 1000;
const pending_saves = new Map();
let save_timer = null;
let saving_stopped = false;

function queueSave(key, value) {
    if (saving_stopped) return;
    pending_saves.set(key, value);
    if (!save_timer) save_timer = setTimeout(flushSaves, SAVE_INTERVAL_MS);
}

export function flushSaves() {
    clearTimeout(save_timer);
    save_timer = null;
    if (saving_stopped) return;
    const saves = [...pending_saves];
    pending_saves.clear();
    for (const [key, value] of saves) saveToStorage(key, value);
}

// For a tab about to reload after its saved content was cleared: any save
// from here on would write the old content back.
export function stopSaving() {
    saving_stopped = true;
    clearTimeout(save_timer);
    save_timer = null;
    pending_saves.clear();
}

if (browser) {
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") flushSaves();
    });
    window.addEventListener("pagehide", flushSaves);
}


export function createLocalStorageStore(key, initialValue) {
    // On the server (prerender/SSR at build time) there is no localStorage.
    // Return a plain writable seeded with the initial value; the browser
    // re-creates it from localStorage on hydration. This is what makes the
    // public pages prerenderable without touching client behavior.
    if (!browser) {
        return writable(initialValue);
    }

    let storedValue = localStorage.getItem(key);
    // console.log("Stored value for " + key + " is " + storedValue);

    if (storedValue === null || storedValue === undefined || storedValue === "undefined") {
        storedValue = initialValue;
    } else {
        try {
            storedValue = JSON.parse(storedValue);
        } catch (e) {
            console.error("Error parsing stored value for " + key, e);
            storedValue = initialValue;
        }
    }

    const store = writable(storedValue);

    store.subscribe(value => {
        queueSave(key, value);
    });

    return store;
}

// Saved by stores that no page reads any more. Removed on every load, because
// a tab still running an older build writes them back. Never reuse these keys.
const RETIRED_KEYS = [
    "terminal_messages", "show_logs", "sources", "hallucination",
    "cookie_consent_given", "file_summary_content", "file_content",
    "selected_file_id", "eligbility_info", "selected_component",
    "selected_question", "general_prompt", "streaming_type", "qualify",
    "qualify_general_instructions", "qualify_user_instructions_to_regenerate",
    "paragraph_to_regenerate", "questions", "regenerated_paragraph", "defend",
    "research_page_selected_tab", "ask_internet_text", "ask_general_text",
    "draft_reasoner_user_general_instructions",
    "innovate_uk_reasoner_user_general_instructions",
];
if (browser) {
    for (const key of RETIRED_KEYS) {
        try { localStorage.removeItem(key); } catch (e) { /* storage unavailable */ }
    }
}






export const email_address = writable("");

export const show_otp = writable(false);
export const show_new_password = writable(false);
export const temporary_token = writable("");

export const result_informed = writable({});







export const authenticated_user = createLocalStorageStore("authenticated_user", null);
export const current_user = createLocalStorageStore("current_user", null);


// Cooper Parry draft verbosity configuration.
// draft_config: user's choice in the Configurations panel: "auto" (let AI decide),
// "stick", "some", or "a_lot". Persisted so it survives reloads.
//
// The default is "a_lot": a user who never opens Configurations gets the most
// elaborate draft and no AI classification call. Anything other than the default
// counts as a deliberate choice and is badged in the toolbar.
export const DRAFT_CONFIG_DEFAULT = "a_lot";
export const DRAFT_LEVELS = ["stick", "some", "a_lot"];
export const draft_config = createLocalStorageStore("draft_config", DRAFT_CONFIG_DEFAULT);

// True when the user has moved away from the default - drives the purple dot on
// the Configurations button. "auto" counts: it is an explicit opt-in to AI choice.
export const draft_config_customised = derived(
    draft_config,
    (v) => v !== DRAFT_CONFIG_DEFAULT,
);

// True once the user has committed a choice in the Configurations panel, even if
// that choice is the default value. draft_config alone cannot tell a deliberate
// "a_lot" from a user who never opened the panel, and the two mean different
// things when reading the usage numbers.
export const draft_config_touched = createLocalStorageStore("draft_config_touched", false);

// Cooper Parry gating lives here rather than in each component so the toolbar
// (which shows the Configurations panel) and the accordion (which resolves the
// level on Regenerate) can never disagree about who the feature applies to.
export const is_cooper_parry_org = derived(authenticated_user, ($u) => {
    const name = ($u?.organization_name ?? $u?.organization?.name ?? "").trim();
    return typeof name === "string" && name.toLowerCase() === "cooper parry";
});

// Beta features switched on for this environment, as reported by GET /me
// (the API's FEATURES env var). Hides menu items and routes; the API refuses
// the gated request types too, so this is not the only guard.
export const enabled_features = derived(authenticated_user, ($u) => new Set($u?.features ?? []));

// Full-detail draft (Beta): switched on per organisation from Super Admin and
// reported back in GET /me's features list, which is the only gate — never the
// organisation name.
export const has_full_detail_draft = derived(enabled_features, ($f) => $f.has("full_detail_draft"));

// Draft with source trace: switched on per organisation from Super Admin. The
// Draft screen becomes the two-pane source trace view and the Standard / Full
// detail switch goes, because the backend always drafts in full detail for it.
export const has_source_trace_draft = derived(enabled_features, ($f) => $f.has("source_trace_draft"));

// draft_mode: "standard" | "full_detail". Persisted per user so two advisors
// sharing a browser do not inherit each other's choice. Re-seeded whenever the
// signed-in user changes; writes go to that user's own key.
export const DRAFT_MODES = [
    { value: "standard", label: "Standard" },
    { value: "full_detail", label: "Full detail" },
];

function draftModeKey(user) {
    return `draft_mode:${user?.id ?? user?.email ?? "anonymous"}`;
}
function readDraftMode(user) {
    if (!browser) return "standard";
    try {
        const raw = localStorage.getItem(draftModeKey(user));
        return raw === "full_detail" ? "full_detail" : "standard";
    } catch (e) {
        return "standard";
    }
}
// EmpowerRD is locked to full detail: the toggle shows Standard as disabled
// and the store refuses to move off full_detail, so a stale localStorage value
// from before the lock can never leak into a request.
function isDraftModeLocked(user) {
    const name = (user?.organization_name ?? user?.organization?.name ?? "").trim();
    return typeof name === "string" && name.toLowerCase() === "empowerrd";
}
function createDraftModeStore() {
    let user = null;
    const inner = writable("standard");
    authenticated_user.subscribe(($u) => {
        user = $u;
        inner.set(isDraftModeLocked(user) ? "full_detail" : readDraftMode(user));
    });
    const set = (value) => {
        const v = value === "full_detail" || isDraftModeLocked(user) ? "full_detail" : "standard";
        if (browser) {
            try { localStorage.setItem(draftModeKey(user), v); } catch (e) { /* storage unavailable */ }
        }
        inner.set(v);
    };
    return { subscribe: inner.subscribe, set, update: (fn) => set(fn(readDraftMode(user))) };
}
export const draft_mode = createDraftModeStore();

export const is_empowerrd_org = derived(authenticated_user, ($u) => isDraftModeLocked($u));

// True when the org cannot switch Draft off full detail (currently EmpowerRD).
export const draft_mode_locked = derived(authenticated_user, ($u) => isDraftModeLocked($u));
export const DRAFT_MODE_LOCKED_HINT = "Standard mode is disabled; please contact support";

// The model the Draft selector starts on. EmpowerRD is deliberately on Terra.
// Which organisation starts where is a product decision and lives here, but it
// belongs in this one store and nowhere else: the toolbar selector, the Draft
// screen and the Realtime document download all read it, so they cannot drift
// apart. The download sends it to the API, where draft questions are generated
// with no selector to read.
export const draft_default_model = derived(is_empowerrd_org, ($empowerrd) =>
    $empowerrd ? "gpt-5.6-terra" : "gpt-5.1-2",
);

// draft_verbosity: the resolved level for the current submit ("stick" | "some" |
// "a_lot"), set just before dispatching the draft questions. Null means "do not
// send a level at all", which is what every non-Cooper-Parry module wants.
export const draft_verbosity = writable(null);

// draft_verbosity_source: who decided that level, sent alongside it so the
// backend rows record it. "auto" = Let AI decide picked it, "manual" = the user
// picked it in Configurations, "default" = the user never touched the panel and
// got DRAFT_CONFIG_DEFAULT. Kept in step with draft_verbosity everywhere: null
// whenever no level is sent.
export const draft_verbosity_source = writable(null);

// Narrative check (source-review highlights, Beta): shows the Configurations
// toggle and, when the user opts in, highlights elaborations upon source
// materials in the rendered draft. The backend runs the review and returns
// source_review either way. Flip to false to park the feature again.
export const NARRATIVE_CHECK_ENABLED = true;

// Revision mode (Revise button with adjust/rewrite on draft questions, plus the
// change badge and in-content change highlights) is parked: hide the Revise
// button and never send base_response/base_mode, so drafts look and behave as
// before. Flip to true to bring the feature back.
export const REVISION_MODE_ENABLED = false;

// Cooper Parry source-review highlights (Beta). Frontend-only gate: the backend
// runs the review and returns source_review either way; this only controls
// whether matched phrases are highlighted in the rendered draft. Default off -
// Cooper Parry users opt in from the Configurations panel.
export const source_review_highlights = createLocalStorageStore("source_review_highlights", false);

// First-login onboarding / guided tour. Persisted so the tour survives the
// full page reloads that happen when navigating between modules.
// Customer support modal (opened from the footer "Support" link, rendered by SupportChat.svelte)
export const support_modal_open = writable(false);

export const tour_active = createLocalStorageStore("tour_active", false);
export const tour_step = createLocalStorageStore("tour_step", 0);

// Cooper Parry "What's new" mini-tour for the Draft Configurations panel.
// Offered on Draft to CP users who have adopted neither Configurations
// feature (no committed verbosity choice, narrative check off). The flash
// card re-offers itself on later visits up to the cap, then never again;
// adopting either feature also stops it. Instructions don't count.
export const CP_CONFIG_TOUR_MAX_SHOWS = 3;
export const cp_config_tour_shows = createLocalStorageStore("cp_config_tour_shows", 0);

// `step` lets the nav open the guide on the section the user is already on.
// Guarded because this is also wired straight to click handlers, which would
// otherwise pass the click event in as the step.
export function startTour(step = 0) {
    tour_step.set(Number.isInteger(step) && step >= 0 ? step : 0);
    tour_active.set(true);
}

// For a sign-in that clears saved content: saving has stopped by then and the
// page is about to load again, so the tour is switched on in storage directly.
export function startTourOnNextLoad() {
    try {
        localStorage.setItem("tour_step", "0");
        localStorage.setItem("tour_active", "true");
    } catch (e) {
        /* ignore storage errors */
    }
}

const ONBOARDING_SEEN_PREFIX = "onboarding_seen_";

export function hasSeenOnboarding(userId) {
    // Default to "seen" when we don't have a user yet, so the guide only
    // ever appears once we can key it to a specific user.
    if (!userId) return true;
    try {
        return localStorage.getItem(ONBOARDING_SEEN_PREFIX + userId) === "true";
    } catch (e) {
        return true;
    }
}

export function markOnboardingSeen(userId) {
    if (!userId) return;
    try {
        localStorage.setItem(ONBOARDING_SEEN_PREFIX + userId, "true");
    } catch (e) {
        /* ignore storage errors */
    }
}





export const question_prompt_edit_open = writable(false);
export const files_list = createLocalStorageStore('files_list', { "draft": [], "defend": [], "research": [], "review": [], "learn": [], "qualify": [], "notify": [] });





export const loading_review = writable(false);


export const review_instructions_modal = writable(false);
export const selected_module = writable("");
export const selected_section = writable("");
export const selected_sub_section = writable("");





export const qualify_add_instructions_modal_open = writable(false);
export const qualify_add_general_instructions_modal_open = writable(false);

export const regenerate_paragraph_modal_open = writable(false);
export const user_instructions_to_regenerate_paragraph = createLocalStorageStore("user_instructions_to_regenerate_paragraph", "");


export const loading_compile = writable(false);





export const loading_defend = writable(false);
export const defend_instructions_modal_open = writable(false);
export const defend_submit_loading = writable(false);


export const loading_project_description = writable(false);
export const project_description_general_prompt_edit = writable(false);
export const project_description_general_prompt = createLocalStorageStore("project_description_general_prompt", "");


export const isDeadlineTabOpen = createLocalStorageStore("isDeadlineTabOpen", true);
export const isDescriptionTabOpen = createLocalStorageStore("isDescriptionTabOpen", true);





export const global_state = createLocalStorageStore("global_state", {

    "draft": {
        "files": {
            "data": {
                "loading": false,
            },
        },
        "uploader": {
            "data": {
                "upload_url": "",
            },
        }
    },

});


// The Admin page used to save a new API client secret here.
if (browser) {
    global_state.update((s) => {
        delete s?.profile?.users?.credentials?.data?.user;
        return s;
    });
}


export const requests_component_lookup = createLocalStorageStore("requests_component_lookup", {});

// The request record routes each server event to the card that sent the
// request. It gained an entry per request and was never trimmed, which is
// what filled heavy users' browser storage. The server replays events for up
// to 24 hours, so entries are kept for 7 days and then dropped. Entries saved
// before they carried a date are dropped too.
const REQUEST_RECORD_DAYS = 7;

export function pruneRequestRecord(record) {
    const cutoff = Date.now() - REQUEST_RECORD_DAYS * 24 * 60 * 60 * 1000;
    for (const [request_id, entry] of Object.entries(record)) {
        if (!(entry?.created_at > cutoff)) delete record[request_id];
    }
    return record;
}

if (browser) requests_component_lookup.update(pruneRequestRecord);

// Dark mode store
export const darkMode = createLocalStorageStore("darkMode", false);